import { 
    Animatable,
    Animation,
    AnimationGroup,
    ArcRotateCamera,
    BoundingInfo,
    DefaultRenderingPipeline,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    GetHotSpotToRef,
    IAnimationKey,
    Mesh,
    MeshBuilder,
    Scene,
    SceneSerializer,
    StandardMaterial,
    Texture,
    Tools
} from "@babylonjs/core";

import type {
    Nullable,
    HotSpotQuery,
    IDisposable,
    AbstractEngine,
    FramingBehavior
} from "@babylonjs/core";


import { Environment } from "./environment";
//import { JSONReader } from "./JSONreader";
import { ModelLoader } from "./modelLoader";
import { PostProcessing } from "./postProcessing";
import { 
    Color3,
    Color4,
    Matrix,
    Vector3,
// eslint-disable-next-line @typescript-eslint/no-unused-vars
    TmpVectors,
// eslint-disable-next-line @typescript-eslint/no-unused-vars
    Viewport
} from "@babylonjs/core/Maths/math";
import Emittery from "emittery";
import { ArcCamera } from "./arcCamera";


function throwIfAborted(...abortSignals: (Nullable<AbortSignal> | undefined)[]): void {
    for (const signal of abortSignals) {
        signal?.throwIfAborted();
    }
}

export type ViewDetails = {
    /**
     * Gets the Viewer instance.
     */
    view: View;

    /**
     * Provides access to the Scene managed by the view.
     */
    scene: Scene;

    /**
     * Provides access to the root mesh of imported models.
     */
    models: Mesh;

    /**
     * Provides access to the main camera.
     */
    camera: ArcRotateCamera;
};

export type ViewOptions = Partial<
    Readonly<{
        onInitialized: (details: Readonly<ViewDetails>) => void;
    }>
>;

/**
 * Information computed from the hot spot surface data, canvas and mesh datas
 */
export type ViewHotSpot = {
    /**
     * 2D canvas position in pixels
     */
    screenPosition: [number, number];
    /**
     * 3D world coordinates
     */
    worldPosition: [number, number, number];
};

export type ViewHotSpotQuery = {
    /**
     * The index of the mesh within the loaded model.
     */
    meshIndex: number;
} & HotSpotQuery;


export interface VariantsSettings{
    DefaultVariant:number,
    ButtonPath:string
}

export interface CameraSettings {
    CameraUpperBetaLimit: number;
    CameraLowerBetaLimit: number;
    CameraUpperRadiousLimit: number;
    CameralowerRadiousLimmit: number;
    CameraUpperAlpha: number;
    CameraLowerAlpha: number;
    CameraAlpha: number;
    CameraBeta: number;
    CameraRadious: number;
}

export class View implements IDisposable {

    //JSONdata = new JSONReader();

    //model loader
    private _modelLoader: ModelLoader;
    public get modelLoader(): ModelLoader {
        return this._modelLoader;
    }

    //environment
    private _environment: Environment;
    public get environment(): Environment {
        return this._environment;
    }

    private readonly _details: ViewDetails; 

    private _isDisposed = false;

    private _notify: Emittery = new Emittery();
    public get notify(): Emittery {
        return this._notify;
    }
    
    public Canvas: HTMLCanvasElement;
    public Container: HTMLElement;
    
    //get only Scene
    // private _scene: Scene;
    // get Scene(): Scene {
    //     return this._scene;
    // }

    //get only Engine
    private _engine: AbstractEngine;
    get Engine(): AbstractEngine {
        return this._engine;
    }

    private _sitePathUri: string;
    get SitePathUri(): string {
        return this._sitePathUri ? this._sitePathUri : (window ? window.location.origin : undefined);
    }
    set SitePathUri(value: string) {
        this._sitePathUri = value;
    }

    private _dataPathUri: string;
    get DataPathUri(): string {
        return this._dataPathUri ? this._dataPathUri : this.SitePathUri;
    }
    set DataPathUri(value: string) {
        this._dataPathUri = value;
    }

    private _assetsPathUri: string;
    get AssetsPathUri(): string {
        return this._assetsPathUri ? this._assetsPathUri : this.DataPathUri;
    }
    set AssetsPathUri(value: string) {
        this._assetsPathUri = value;
    }

    private _postProcessingClass: PostProcessing;
    get PostProcessingClass(): PostProcessing {
        return this._postProcessingClass;
    }

    private _pipeline:  DefaultRenderingPipeline;

    public ArcCamera: ArcCamera;    
    public IsReady: boolean = false;


    private _objectUrl: string;
    private _turntableAnimatable: Animatable;

    private _model:string
    //private _environment:string;
    private _cameraSettings: CameraSettings
    private _variantSettings:VariantsSettings
    private _singleRepeat:boolean = false;
    private _fullPageView:boolean;
    private _text1:string="GLB";
    private _text2:string="SW";
    private _backgroundColor:string;

    private _canvasWidth:string = "100px";
    private _canvasHeight:string = "100px";


    private _textpadding1Top:number;
    private _textpadding2Top:number;
    private _textpadding1Right:number;
    private _textpadding2Right:number;
    private _textFontSize1:number;
    private _textFontSize2:number;

    private _uniqueId: string;
    get UniqueId(): string {
        return this._uniqueId;
    }

    public constructor(uniqueId?: string)
    {
        this._uniqueId = uniqueId;
        this._details = {
            view: this,
            scene: null,
            models: null,
            camera: null,
        };

    }

    public static async CreateView(
        engine: AbstractEngine,
        canvas: HTMLCanvasElement,
        container: HTMLElement,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        options?: ViewOptions
    ): Promise<View>
    {
        const view = new View();
        view._engine = engine;
                
        await view.CreateScene(canvas, container);
        return view;
    }

    public dispose(): void {
        this._isDisposed = true;
    }
    
    // async createEngine() {
    //     const webGPUSupported = await WebGPUEngine.IsSupportedAsync;
    //     if (webGPUSupported) {
    //       const engine = new WebGPUEngine(this.Canvas);
    //       await engine.initAsync();
    //       return engine;
    //     }
    //     return new Engine(this.Canvas, true);
    //   }

    public GetModelPosition(modelId: string): Vector3 {
        const model = this._modelLoader.GetModelById(modelId);
        return model?.position;
    }

    public GetLoadedModelsIDs(): string[] {
        const modelIDs: string[] = [];
        this._details.models.getChildMeshes(true).forEach((model) => {
            modelIDs.push(model.id);
        });

        return modelIDs;
    }

    public async CreateScene(canvas: HTMLCanvasElement, container: HTMLElement)
    {
        this.Canvas = canvas;
        this.Container = container; //document.getElementById( "v" + this._uniqueId ) as HTMLDivElement;

        if (!this._engine)
        {
            const { Engine } = await import("@babylonjs/core/Engines/engine");

            this._engine = new Engine(this.Canvas, true, {stencil:true})
        }
        
        Tools.LogLevels = Tools.WarningLogLevel;

        //scene
        this._details.scene = new Scene(this.Engine);
        this._details.scene.useRightHandedSystem=true;

        //root mesh for all imported models
        const modelsRoot = new Mesh("Models", this._details.scene);
        this._details.models = modelsRoot;

        //default pipeline
        this._pipeline = new DefaultRenderingPipeline(
            "amytisDefaultRenderingPipeline", // The name of the pipeline
            true, // Do you want the pipeline to use HDR texture?
            this._details.scene // The scene instance
            //cameras The array of cameras that the rendering pipeline will be attached to (default: scene.cameras)
            //automaticBuild If false, you will have to manually call prepare() to update the pipeline (default: true)
        );        

        this._details.camera = new ArcRotateCamera("RootCamera", 0, 0, 1, Vector3.Zero(), this._details.scene);
        // Enable camera's behaviors
        this._details.camera.useFramingBehavior = true;
        const framingBehavior = this._details.camera.getBehaviorByName("Framing") as FramingBehavior;
        framingBehavior.framingTime = 0;
        framingBehavior.elevationReturnTime = -1;

        //this._details.camera = new ArcRotateCamera("RootCamera",Math.PI,0,2,Vector3.Zero(),this._details.scene);
        //this.Camera.wheelPrecision=20
        if (!Object.prototype.hasOwnProperty.call(AnimationGroup.prototype, "currentFrame")) {
            Object.defineProperty(AnimationGroup.prototype, "currentFrame", {
                get () {        
                    return this.targetedAnimations[0]?.animation.runtimeAnimations[0]?.currentFrame ?? 0;
                },
                set (frame) {
                    this.goToFrame(frame);
                }
            });
        }

        //this.CreateDefaultScene();
        this._cameraSettings = {
            CameraUpperBetaLimit:2.355,
            CameraLowerBetaLimit:0.785,
            CameralowerRadiousLimmit:0,
            CameraUpperRadiousLimit:20,
            CameraLowerAlpha:3.14,
            CameraUpperAlpha:3.14,
            CameraAlpha:3.14,
            CameraBeta:2,
            CameraRadious:10
        }
        this._variantSettings={
            DefaultVariant:1,
            ButtonPath:""
        }

        this.ArcCamera = new ArcCamera(this._cameraSettings, this._details.camera, this.Canvas);

        console.log("Creating Default Scene")
        
        this._postProcessingClass = new PostProcessing(this._details);
        this._environment = new Environment(this, this._details.scene, this._modelLoader)
        this._environment.BackgroundPath = this.ResolveAssetsPath("./assets/Default/Environments/Default.env");
        this._environment.ApplyEnvironment();
        this._modelLoader = new ModelLoader(
            this._variantSettings,
            this._cameraSettings,
            false, //this.JSONdata.SingleRepeatAnimation,
            this._details
            );


         // run the main render loop
        this.Engine.runRenderLoop(() => {
            if(!this.IsReady)
            this.IsReady = true;
            this._details.scene.render();
        });
        window.addEventListener('resize', function(){
            this.Engine.resize();
            if(this.LoadingDiv != null)
            {
                this.LoadingDiv.style.width =this._view.Canvas.width.toString()+"px";
                this.LoadingDiv.style.height =this._view.Canvas.height.toString()+"px";
            }
        }.bind(this));
        this.Canvas.addEventListener('wheel', evt => evt.preventDefault());
        //this.ToggleInspector();
    }
    public SerializeScene(filename: string)
    {        
        const serializedScene = SceneSerializer.Serialize(this._details.scene);
            
        const strMesh = JSON.stringify(serializedScene);
        
        if (filename.toLowerCase().lastIndexOf(".babylon") !== filename.length - 8 || filename.length < 9){
            filename += ".babylon";
        }
                
        const blob = new Blob ( [ strMesh ], { type : "octet/stream" } );
           
        // turn blob into an object URL; saved as a member, so can be cleaned out later
        const objectUrl = (window.webkitURL || window.URL).createObjectURL(blob);
        
        const link = window.document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        const click = document.createEvent("MouseEvents");
        click.initEvent("click", true, false);
        link.dispatchEvent(click);    
    }
    
    async publicCreateGUI()
    {
    }
    public Optimizatinos()
    {
    //     var options = new SceneOptimizerOptions(60)
    //     options.addOptimization(new ShadowsOptimization())
    //     options.addOptimization(new LensFlaresOptimization())
    //     options.addOptimization(new PostProcessesOptimization())
    //     options.addOptimization(new ParticlesOptimization())
    //    options.addOptimization(new TextureOptimization())
    //    options.addOptimization(new RenderTargetsOptimization())
    //     var optimizer = new SceneOptimizer(this.Scene,options);
    //     optimizer.start();        
        
        //this.Scene.performancePriority = ScenePerformancePriority.Aggressive;
    }
    public Remove()
    {
        this._details.scene?.dispose();
        this._engine?.dispose();
        this._details.scene=null
        this._engine=null;
    }
    
    
    convertHex(hex: string, opacity = 1):Color4
    {
        if(!hex)
            return new Color4(1,1,1,1);
            // Remove the hash at the start if it's there
        hex = hex.replace(/^#/, '');

        // Parse the r, g, b values
        const r = parseInt(hex.substring(0, 2), 16) / 255;
        const g = parseInt(hex.substring(2, 4), 16) / 255;
        const b = parseInt(hex.substring(4, 6), 16) / 255;

        // Return the Color4 object with alpha set to 1
        return new Color4(r,g,b,opacity);
    }
    public get BackgroundColor():string{return this._backgroundColor;}
    public set BackgroundColor(value:string)
    {
        if(value)
        {
            this._backgroundColor = value
            this._details.scene.clearColor=this.convertHex( this._backgroundColor,1);
        }
    }


    // public get Environment():string{return this._environment;}
    // public set Environment(value:string)
    // {
    //     if(value)
    //     {
    //         this._environment = value
    //         this.EnvironmentClass.ApplyEnvironment(value)
    //     }
    //     else
    //     {
    //         this._environment = null
    //         this.Scene.clearColor=this.convertHex( this._backgroundColor,1);
    //     }
    // }
    public get SingleRepeatAnimation():boolean{return this._singleRepeat}
    public set SingleRepeatAnimation(value:boolean)
    {
        this._singleRepeat=value;
        if(this._singleRepeat && this._modelLoader.Animations.length > 0)
            this._modelLoader.Animations[0].play(true);
        else
        {
            if(this._modelLoader.Animations.length > 0)
            {
                this._modelLoader.Animations[0].stop();
                this._modelLoader.Animations[0].reset()
            }
        }
    }

    public get CanvasWidth(): string {
        return this._canvasWidth;
    }
    public set CanvasWidth(value: string)
    {
        if(value !== this._canvasWidth)
        {
            this._canvasWidth = value

            if (this.Container)
                this.Container.style.width = value;
            else
                this.Canvas.style.width = value;

            this._engine.resize();
        }
    }

    public get CanvasHeight(): string {
        return this._canvasHeight
    }
    public set CanvasHeight(value: string)
    {
        if(value !== this._canvasHeight)
        {
            if (this.Container)
                this.Container.style.height = value;
            else
                this.Canvas.style.height = value;  
            
            this._engine.resize();
          
        }
    }

    public get CanvasFullPage():boolean{return this._fullPageView}
    public set CanvasFullPage(value:boolean)
    {
        this._fullPageView=value;
        if(this._fullPageView)
        {
            const outer = document.getElementsByClassName("outer")[0] as HTMLDivElement

            this.Container.style.width = "100vw"
            this.Container.style.height= "100vh"
            outer.style.width = "100vw"
            outer.style.height= "100vh"
            outer.style.left="0px"
            document.body.style.overflow="hidden"
            if( this._modelLoader.LoadingDiv != null)
            {
                this._modelLoader.DeleteLoadingScreen();
                this._modelLoader.LoadLoadingScreen();
                this._modelLoader.LoadingDiv.style.height= this.Canvas.style.height;
            }
            this.Engine.resize();
        }
        else
        {
            this.Canvas.style.width = this._canvasWidth;
            this.Canvas.style.height = this._canvasHeight;
            if( this._modelLoader.LoadingDiv != null)
            {
                this._modelLoader.DeleteLoadingScreen();
                this._modelLoader.LoadLoadingScreen();
                this._modelLoader.LoadingDiv.style.height= this.Canvas.style.height;
            }
            this.Engine.resize();
        }
    }

    public StopTurntableAnimation()
    {        
        if (this._turntableAnimatable)
        {
            this._turntableAnimatable.stop();
            this._turntableAnimatable.reset();
            this._details.models.position = new Vector3(0,0,0);
            this._details.models.rotation.y = 0;
            this._details.models.setPivotMatrix(Matrix.Identity());
            // if(process.env.NODE_ENV === "development")
            // {
            //     const box = this._details.scene.getMeshByName("__rootTransformBox__");
            //     if (box)
            //         this._details.scene.removeMesh(box);
            // }
        }
    }

    public StartTurntableAnimation()
    {
        this.StopTurntableAnimation();

        // force the update
        // Updates the bounding info of a parent mesh
        const getParentUpdatedBoundingInfo = (parent: Mesh) => {
            parent.getChildMeshes().forEach(mesh => mesh.computeWorldMatrix(true)) // force the update
            const childMeshes = parent.getChildMeshes();
            let min = childMeshes[0].getBoundingInfo().boundingBox.minimumWorld;
            let max = childMeshes[0].getBoundingInfo().boundingBox.maximumWorld;
            for(let i=0; i<childMeshes.length; i++){
                const meshMin = childMeshes[i].getBoundingInfo().boundingBox.minimumWorld;
                const meshMax = childMeshes[i].getBoundingInfo().boundingBox.maximumWorld;

                min = Vector3.Minimize(min, meshMin);
                max = Vector3.Maximize(max, meshMax);
            }
            return new BoundingInfo(min, max);
        }        
        
        const boundingInfo = getParentUpdatedBoundingInfo(this._details.models);
        const boundingBox = boundingInfo.boundingBox;
        
        //draw debug bounding box
        // if(process.env.NODE_ENV === "development")
        // {
        //     const size = boundingBox.maximumWorld.subtract(boundingBox.minimumWorld);
            
        //     const box = MeshBuilder.CreateBox("__rootTransformBox__", {width: size.x, height: size.y, depth: size.z});
        //     box.position = boundingBox.centerWorld;

        //     const mat = new StandardMaterial("mat");
        //     mat.diffuseColor = new Color3(1,1,0);
        //     mat.wireframe = true;

        //     box.material = mat;
        //     //end bounding box
        // }

        //set pivot
        this._details.models.setPivotMatrix(
            Matrix.Translation(
                -boundingBox.centerWorld.x, -boundingBox.centerWorld.y, -boundingBox.centerWorld.z
            ));

        //turntable animation
        const startFrame = 0;
        const endFrame = 100;
        const frameRate = 10;
    
        const turntableAnimation = new Animation(
            "__turntableAnimation__", "rotation.y", frameRate, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CYCLE
        );
    
        const keyFrames: Array<IAnimationKey> = []; 
    
        keyFrames.push({
            frame: startFrame,
            value: 0
        });
    
        keyFrames.push({
            frame: endFrame,
            value: 2*Math.PI
        });
    
        turntableAnimation.setKeys(keyFrames);

        this._turntableAnimatable = this._details.scene.beginDirectAnimation(
            this._details.models, [turntableAnimation], startFrame, endFrame, true
            );
        
    } 
    /**
     * Toggle the Babylon almighty inspector
     */
    public ToggleInspector(parent?: HTMLElement): Promise<void> {
        // Rely on code splitting to prevent all of babylon
        // + loaders, serializers... to be downloaded if not necessary
        return import(/* webpackChunkName: "debug" */ "./debug/inspector").then((inspectorModule) => {
            inspectorModule.toggleInspector(this._details.scene, parent);
        });
    }

    //accepts absolute or relative path to data
    public ResolveDataPath(uri: string): string {
        
        //const normalizedUri = decodeURI(uri); //aka fix
        const normalizedUri = uri;        

        if (this.DataPathUri)
            return decodeURIComponent(new URL(normalizedUri, this.DataPathUri).href);

        return decodeURIComponent(normalizedUri);
    }

    //accepts absolute or relative path to data
    public ResolveAssetsPath(uri: string): string {
    
        //const normalizedUri = decodeURI(uri); //aka fix
        const normalizedUri = uri;

        if (this.AssetsPathUri)
            return decodeURIComponent(new URL(normalizedUri, this.AssetsPathUri).href);

        return decodeURIComponent(normalizedUri);
    }

    public GetFilenameFromUri(uri: string) {
        // Replace backslashes with forward slashes
        //const normalizedUri = decodeURI(uri); //aka fix
        const normalizedUri = uri;

        let filename = normalizedUri.split('\\').pop();
        filename = filename.split('/').pop();
        return decodeURIComponent(filename);
    }
    
    public GetFilenameExtensionFromUri(uri: string) {
        if (!uri) return "";
        // Replace backslashes with forward slashes
        //const normalizedUri = decodeURI(uri); //aka fix
        const normalizedUri = uri;

        const filenameExtension = normalizedUri.split('.').pop();
        return decodeURIComponent(filenameExtension).toLowerCase();
    }

    public async DownloadScene(filename: string)  {

        if (this._objectUrl)
            window.URL.revokeObjectURL(this._objectUrl);
        
        const oldForceSerializeBuffers = Texture.ForceSerializeBuffers;
        Texture.ForceSerializeBuffers = true;
        let serializedScene;
        try {
            serializedScene = await SceneSerializer.SerializeAsync(this._details.scene);
        } catch (error) {
            console.error(error);
            return;
        }
        finally {
            Texture.ForceSerializeBuffers = oldForceSerializeBuffers;
        }
                            
        const strMesh = JSON.stringify(serializedScene);
        
        if (filename.toLowerCase().lastIndexOf(".babylon") !== filename.length - 8 || filename.length < 9){
            filename += ".babylon";
        }
                
        const blob = new Blob ( [ strMesh ], { type : "octet/stream" } );
           
        // turn blob into an object URL; saved as a member, so can be cleaned out later
        this._objectUrl = (window.webkitURL || window.URL).createObjectURL(blob);
        
        const link = window.document.createElement('a');
        link.href = this._objectUrl;
        link.download = filename;

        const click = new MouseEvent("click", {"bubbles":true, "cancelable":false});
        link.dispatchEvent(click);
    }    

    /**
     * Check for disposed or aborted state (basically everything that can interrupt an async operation).
     * @param abortSignals A set of optional AbortSignals to also check.
     */
    private _throwIfDisposedOrAborted(...abortSignals: (Nullable<AbortSignal> | undefined)[]): void {
        if (this._isDisposed) {
            throw new Error("View is disposed.");
        }

        throwIfAborted(...abortSignals);
    }    

    /**
     * retrun world and canvas coordinates of an hot spot
     * @param hotSpotQuery mesh index and surface information to query the hot spot positions
     * @param res Query a Hot Spot and does the conversion for Babylon Hot spot to a more generic HotSpotPositions, without Vector types
     * @returns true if hotspot found
     */
    public getHotSpotToRef(hotSpotQuery: Readonly<ViewHotSpotQuery>, res: ViewHotSpot): boolean {
        //TODO: remove Model map in ModelLoader
        //if (!this._details.modelLoader.Models) {
        //    return false;
        //}
        // const worldPos = TmpVectors.Vector3[1];
        // const screenPos = TmpVectors.Vector3[0];
        // const mesh = this._details.modelLoader.Model.meshes[hotSpotQuery.meshIndex];
        // if (!mesh) {
        //     return false;
        // }
        // GetHotSpotToRef(mesh, hotSpotQuery, worldPos);

        // const renderWidth = this._engine.getRenderWidth(); // Get the canvas width
        // const renderHeight = this._engine.getRenderHeight(); // Get the canvas height

        // const viewportWidth = this.Camera.viewport.width * renderWidth;
        // const viewportHeight = this.Camera.viewport.height * renderHeight;
        // const scene = this._details.scene;

        // Vector3.ProjectToRef(worldPos, mesh.getWorldMatrix(), scene.getTransformMatrix(), new Viewport(0, 0, viewportWidth, viewportHeight), screenPos);
        // res.screenPosition = [screenPos.x, screenPos.y];
        // res.worldPosition = [worldPos.x, worldPos.y, worldPos.z];
        return true;
    }

}